import { Injectable } from '@angular/core';
import { Actions, concatLatestFrom, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import {
    loadDocumentTypeRequirementDescriptionsSuccess,
    loadDocumentTypeTextRequirementsFailure,
    loadDocumentTypeTextRequirementsSuccess,
    searchDocumentTypesByName,
    searchDocumentTypesByNameFailure,
    searchDocumentTypesByNameSuccess,
    selectedDocumentType
} from 'src/app/compliance/store/actions/document-reviewer.actions';
import {
    selectComplianceCandidateDocument,
    selectComplianceContractSelected
} from 'src/app/compliance/store/selectors/compliance.selectors';
import { RequirementNSwagMigrationService } from '../../api/migration/requirement-nswag-migration.service';
import { DocumentTypeConfiguration } from '../../models/requirement-descriptions.model';
import { DocumentTypesClientWrapper } from '../../document-types-client-wrapper';
import { DocumentTypeRequirementTextDto } from '../../api/models/document-type-requirement-text-dto.model';

@Injectable()
export class DocumentTypesEffects {
    constructor(
        private readonly _actions$: Actions,
        private readonly _documentTypesService: DocumentTypesClientWrapper,
        private readonly _requirementService: RequirementNSwagMigrationService,
        private readonly _store: Store
    ) {}

    searchDocumentTypesByName$ = createEffect(() => {
        return this._actions$.pipe(
            ofType(searchDocumentTypesByName),
            concatLatestFrom(() => [
                this._store.select(selectComplianceContractSelected),
                this._store.select(selectComplianceCandidateDocument)
            ]),
            switchMap(([action, contract, document]) => {
                if (contract && document) {
                    return this._documentTypesService
                        .searchDocumentTypes(
                            contract.lineOfBusinessId ?? document.lineOfBusinessId,
                            action.searchText,
                            contract.facilityId
                        )
                        .pipe(
                            map((types) => searchDocumentTypesByNameSuccess({ types: types })),
                            catchError((result) => of(searchDocumentTypesByNameFailure({ error: result })))
                        );
                } else {
                    return of(searchDocumentTypesByNameSuccess({ types: [] }));
                }
            })
        );
    });

    getDocumentTypeTextRequirements$ = createEffect(() => {
        return this._actions$.pipe(
            ofType(selectedDocumentType),
            concatLatestFrom(() => this._store.select(selectComplianceContractSelected)),
            switchMap(([action, contract]) => {
                if (action.documentType?.hasRequirements && contract?.facilityId) {
                    return this._documentTypesService
                        .getDocumentTypeTextRequirements(action.documentType.id, contract.facilityId)
                        .pipe(
                            map((result: DocumentTypeRequirementTextDto[]) =>
                                loadDocumentTypeTextRequirementsSuccess({ documentTextRequirements: result })
                            ),
                            catchError((error) => of(loadDocumentTypeTextRequirementsFailure({ error: error })))
                        );
                } else {
                    return of(loadDocumentTypeTextRequirementsSuccess({ documentTextRequirements: [] }));
                }
            })
        );
    });

    getDocumentTypeRequirementsDescriptionByContractId$ = createEffect(() => {
        return this._actions$.pipe(
            ofType(selectedDocumentType),
            concatLatestFrom(() => this._store.select(selectComplianceContractSelected)),
            switchMap(([action, contract]) => {
                if (action.documentType?.hasRequirements && contract?.facilityId) {
                    return this._requirementService
                        .getDocumentTypeRequirementDescriptionByContractId(
                            action.documentType.id,
                            contract.id,
                            true,
                            false
                        )
                        .pipe(
                            map((result: DocumentTypeConfiguration[]) =>
                                loadDocumentTypeRequirementDescriptionsSuccess({
                                    documentTypeRequirementDescriptions: result
                                })
                            ),
                            catchError((error) => of(loadDocumentTypeTextRequirementsFailure({ error: error })))
                        );
                } else {
                    return of(
                        loadDocumentTypeRequirementDescriptionsSuccess({ documentTypeRequirementDescriptions: [] })
                    );
                }
            })
        );
    });
}
